// web/src/components/admin/JobList.tsx

import { useState, useEffect } from 'react';
import {
  Clock,
  CheckCircle,
  XCircle,
  Loader,
  AlertTriangle,
  FileText,
  RefreshCw,
  StopCircle,
} from 'react-feather';
import { PipelineJob, PipelineJobListResponse, PipelineJobStatus, PipelineMode } from '../../types/pipeline';

interface JobListProps {
  onSelectJob?: (job: PipelineJob) => void;
  refreshTrigger?: number;
  totalSourceCount?: number;
  environment?: string;
  onJobCancelled?: () => void;
}

const MODE_DISPLAY: Record<PipelineMode, string> = {
  'clear-and-update': 'Clear and Update',
  'clear-only': 'Clear Only',
  'update-only': 'Update Only',
};

export default function JobList({ onSelectJob, refreshTrigger, totalSourceCount = 0, environment, onJobCancelled }: JobListProps) {
  const [jobs, setJobs] = useState<PipelineJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingJobId, setCancellingJobId] = useState<string | null>(null);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/pipeline/jobs?limit=20');
      const data: PipelineJobListResponse = await response.json();

      if (data.success) {
        // Filter by environment if specified
        const filteredJobs = environment
          ? data.data.filter(job => job.environment === environment)
          : data.data;
        setJobs(filteredJobs);
      } else {
        setError('Failed to load jobs');
      }
    } catch (err) {
      setError('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const cancelJob = async (jobId: string) => {
    try {
      setCancellingJobId(jobId);
      const response = await fetch(`/api/admin/pipeline/jobs/${jobId}/cancel`, {
        method: 'POST',
      });
      const data = await response.json();

      if (data.success) {
        fetchJobs();
        onJobCancelled?.();
      } else {
        setError(data.error || 'Failed to cancel job');
      }
    } catch (err) {
      setError('Failed to cancel job');
    } finally {
      setCancellingJobId(null);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [refreshTrigger, environment]);

  const getStatusBadge = (status: PipelineJobStatus) => {
    const configs: Record<
      PipelineJobStatus,
      { icon: React.ReactNode; bg: string; text: string; label: string }
    > = {
      pending: {
        icon: <Loader className="animate-spin" size={14} />,
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        label: 'Pending',
      },
      running: {
        icon: <Loader className="animate-spin" size={14} />,
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        label: 'Running',
      },
      completed: {
        icon: <CheckCircle size={14} />,
        bg: 'bg-green-100',
        text: 'text-green-800',
        label: 'Completed',
      },
      failed: {
        icon: <XCircle size={14} />,
        bg: 'bg-red-100',
        text: 'text-red-800',
        label: 'Failed',
      },
      cancelled: {
        icon: <AlertTriangle size={14} />,
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        label: 'Cancelled',
      },
    };

    const config = configs[status];
    return (
      <span
        className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        {config.icon}
        <span>{config.label}</span>
      </span>
    );
  };

  const formatDuration = (job: PipelineJob): string => {
    if (!job.startedAt) return '-';

    const start = new Date(job.startedAt).getTime();
    const end = job.completedAt ? new Date(job.completedAt).getTime() : Date.now();
    const durationMs = end - start;

    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);

    if (minutes === 0) {
      return `${seconds}s`;
    }
    return `${minutes}m ${seconds}s`;
  };

  const formatTime = (isoString: string): string => {
    return new Date(isoString).toLocaleString();
  };

  const formatSourcesDetail = (job: PipelineJob): string => {
    if (job.sources === 'all') {
      return totalSourceCount > 0 ? `All Sources (${totalSourceCount})` : 'All Sources';
    }
    const sourceCount = job.sources.length;
    const suffix = totalSourceCount > 0 ? ` of ${totalSourceCount}` : '';
    if (sourceCount <= 2) {
      return `${job.sources.join(', ')} (${sourceCount}${suffix})`;
    }
    return `${job.sources.slice(0, 2).join(', ')} (${sourceCount}${suffix})`;
  };

  const formatSteps = (job: PipelineJob): string => {
    if (job.steps === 'all') {
      return 'All Steps (4)';
    }
    const steps = job.steps as string[];
    const stepCount = steps.length;
    const capitalizedSteps = steps.map((s) => s.charAt(0).toUpperCase() + s.slice(1));
    if (stepCount === 4) {
      return 'All Steps (4)';
    }
    return `${capitalizedSteps.join(', ')} (${stepCount} of 4)`;
  };

  const formatMode = (job: PipelineJob): string => {
    return MODE_DISPLAY[job.mode] || job.mode;
  };

  if (loading && jobs.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center text-gray-500">
          <Loader className="animate-spin mr-2" size={20} />
          Loading job history...
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Recent Jobs</h3>
        <button
          onClick={fetchJobs}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          title="Refresh"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {error && (
        <div className="px-6 py-3 bg-red-50 text-red-700 text-sm">{error}</div>
      )}

      {jobs.length === 0 ? (
        <div className="px-6 py-12 text-center text-gray-500">
          <FileText className="mx-auto mb-4 text-gray-300" size={48} />
          <p>No pipeline jobs found</p>
          <p className="text-sm mt-1">Run the pipeline to see job history here</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-200">
          {jobs.map((job) => (
            <div
              key={job.id}
              className="px-6 py-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  {/* First row: Status, ID, Environment, Duration, Date */}
                  <div className="flex items-center space-x-3">
                    {getStatusBadge(job.status)}
                    <span className="text-sm font-mono text-gray-500">
                      {job.id.substring(0, 8)}...
                    </span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        job.environment === 'production'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {job.environment.toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-500 flex items-center">
                      <Clock size={12} className="mr-1" />
                      {formatDuration(job)}
                    </span>
                    <span className="text-xs text-gray-500">{formatTime(job.createdAt)}</span>
                  </div>

                  {/* Second row: Job configuration details */}
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                    <span>
                      <span className="font-medium text-gray-500">Sources:</span>{' '}
                      {formatSourcesDetail(job)}
                    </span>
                    <span>
                      <span className="font-medium text-gray-500">Steps:</span>{' '}
                      {formatSteps(job)}
                    </span>
                    <span>
                      <span className="font-medium text-gray-500">Mode:</span>{' '}
                      {formatMode(job)}
                    </span>
                    <span className="text-gray-400">by {job.createdBy}</span>
                  </div>

                  {job.error && (
                    <div className="mt-2 text-xs text-red-600 truncate">
                      {job.error}
                    </div>
                  )}

                  {job.progress && job.status === 'running' && (
                    <div className="mt-2 text-xs text-blue-600">
                      <span className="capitalize">{job.progress.currentStep}</span>
                      {job.progress.currentSource && ` - ${job.progress.currentSource}`}
                      {' '}({job.progress.completedSources.length}/{job.progress.totalSources} sources)
                    </div>
                  )}
                </div>

                <div className="ml-4 flex items-center space-x-2">
                  {(job.status === 'running' || job.status === 'pending') && (
                    <button
                      onClick={() => cancelJob(job.id)}
                      disabled={cancellingJobId === job.id}
                      className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors flex items-center space-x-1 disabled:opacity-50"
                      title="Cancel job"
                    >
                      {cancellingJobId === job.id ? (
                        <Loader className="animate-spin" size={14} />
                      ) : (
                        <StopCircle size={14} />
                      )}
                      <span>Cancel</span>
                    </button>
                  )}
                  <button
                    onClick={() => onSelectJob?.(job)}
                    className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    View Logs
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
